/**
 * PersonalResidenceTriView — Phase 48 header block.
 *
 * Owner-occupant deals get three purpose-built KPI tiles instead of
 * the buyer-side Asking / Counter / Target scenario grid. The KPIs
 * that matter for a home you'll live in are:
 *   1. Monthly Cost (PITI + HOA + maintenance)
 *   2. Break-Even Years (buy vs rent — the decision anchor)
 *   3. 5-Year Equity (principal paydown + appreciation)
 *
 * This component is intentionally small and stateless. The heavy
 * lifting (three-path scenarios, AI verdict, tax modelling) lives in
 * `PersonalResidenceScenarios` right below it.
 */
import { Home, Calendar, TrendingUp } from "lucide-react";

function fmtMoney(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return `$${Math.round(Number(n)).toLocaleString("en-US")}`;
}

const Tile = ({ label, value, sub, tone = "slate", Icon, testid }) => {
  const toneClass = {
    slate: "bg-slate-50 text-slate-900",
    teal: "bg-teal-50 text-teal-900",
    indigo: "bg-indigo-50 text-indigo-900",
    emerald: "bg-emerald-50 text-emerald-900",
  }[tone];
  return (
    <div
      data-testid={testid}
      className={`flex flex-col rounded-xl border border-slate-200 p-4 shadow-sm ${toneClass}`}
    >
      <div className="mb-2 flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 opacity-70" /> : null}
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-70">
          {label}
        </p>
      </div>
      <p className="text-3xl font-bold tabular-nums">{value}</p>
      {sub ? <p className="mt-1 text-xs opacity-70">{sub}</p> : null}
    </div>
  );
};

export default function PersonalResidenceTriView({ deal }) {
  if (!deal) return null;
  const monthlyCost = deal.monthly_cost;
  const breakEven = deal.break_even_years_vs_rent;
  const equity5 = deal.equity_5yr;
  const rentEq = deal.monthly_rent_equivalent;
  const isFL = deal.is_florida;
  const verdict = deal.verdict;

  return (
    <section
      data-testid="personal-residence-triview"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <header className="mb-4 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-baseline">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-teal-600">
            Personal Residence
            {isFL ? " · FL Homestead" : ""}
            {verdict ? ` · ${verdict}` : ""}
          </p>
          <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-900">
            {breakEven
              ? `Break-even vs renting: ${breakEven} year${breakEven === 1 ? "" : "s"}`
              : "Owner-occupant analysis"}
          </h2>
        </div>
        {rentEq ? (
          <p className="text-xs text-slate-500">
            Rent equivalent: <span className="tabular-nums font-medium">{fmtMoney(rentEq)}/mo</span>
          </p>
        ) : null}
      </header>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Tile
          testid="pr-tile-monthly-cost"
          label="Monthly Cost"
          value={fmtMoney(monthlyCost)}
          sub="PITI + HOA + maintenance"
          tone="slate"
          Icon={Home}
        />
        <Tile
          testid="pr-tile-break-even"
          label="Break-Even vs Rent"
          value={breakEven ? `${breakEven} yr${breakEven === 1 ? "" : "s"}` : "—"}
          sub={rentEq ? `vs ${fmtMoney(rentEq)}/mo rent` : "no rent input"}
          tone="indigo"
          Icon={Calendar}
        />
        <Tile
          testid="pr-tile-equity-5yr"
          label="5-Year Equity"
          value={fmtMoney(equity5)}
          sub="principal + appreciation"
          tone="emerald"
          Icon={TrendingUp}
        />
      </div>
    </section>
  );
}
